import { useEffect, useRef } from "react";
import { GOOGLE_CLIENT_ID, BASE_URL } from "@/utilities/configurations";
import { useLoaderStore } from "@/zustand/loaderStore";
import { useNotificationStore } from "@/zustand/notificationsStore";
import "./GoogleAuthButton.css";

interface GoogleAuthButtonProps {
  text: "signin_with" | "signup_with";
}

const GoogleAuthButton = ({ text }: GoogleAuthButtonProps) => {
  const { setLoading } = useLoaderStore((state) => state);
  const btnRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.onload = () => {
      google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async (response: google.accounts.id.CredentialResponse) => {
          setLoading(true);
          try {
            const res = await fetch(`${BASE_URL}/auth/token-login`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ token: response.credential }),
            });
            if (res.status === 403) {
              setLoading(false);
              useNotificationStore
                .getState()
                .error("Your account is inactive. Please contact support.");
              return;
            }
            if (!res.ok) {
              setLoading(false);
              useNotificationStore
                .getState()
                .error(
                  "An error occurred during Google authentication. Please try again."
                );
              return;
            }
            const body = (await res.json()) as {
              hash: string | null;
              validUntil: string | null;
            };
            if (body.hash) {
              localStorage.setItem("opt-token", JSON.stringify(body.hash));
              window.location.href = "/role";
            } else {
              window.location.href = `/auth/google-signup?token=${encodeURIComponent(response.credential)}`;
            }
          } catch {
            setLoading(false);
            useNotificationStore
              .getState()
              .error(
                "An error occurred during Google authentication. Please try again."
              );
          }
        },
      });
      if (btnRef.current) {
        google.accounts.id.renderButton(btnRef.current, {
          type: "standard",
          theme: "outline",
          size: "large",
          text,
          width: btnRef.current.offsetWidth,
        });
      }
    };
    document.body.appendChild(script);

    return () => {
      script.remove();
    };
  }, []);

  return <div ref={btnRef} className="google-auth-btn" />;
};

export default GoogleAuthButton;
